import { type RecordId } from 'javascripts/shared-types';
import ApplicationModel, { attr } from '../ApplicationModel';
import DateRangeFilter from '../filters/DateRangeFilter';
import FeatureTypeFilter from '../filters/FeatureTypeFilter';
import InitiativeFilter from '../filters/InitiativeFilter';
import ReleaseFilter from '../filters/ReleaseFilter';
import UserFilter from '../filters/UserFilter';
import Fragment from '../Fragment';
import { model } from '../modelBuilder';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import { type BookmarksProductValueChartAttributes } from './generated/BookmarksProductValueChartAttributes';
import Initiative from './Initiative';
import Project from './Project';
import Release from './Release';
import User from './User';
import ProjectFilter from '../filters/ProjectFilter';
import TeamFilter from '../filters/TeamFilter';

export const TimeGroupings = {
  MONTHS: 'months',
  WEEKS: 'weeks',
  ITERATIONS: 'iterations',
};

interface BookmarksProductValueChart
  extends BookmarksProductValueChartAttributes,
    HasBookmarkFilters<{
      dateRange: DateRangeFilter;
      releases: ReleaseFilter;
      projects: ProjectFilter;
      teams: TeamFilter;
      initiatives: InitiativeFilter;
      featureTypes: FeatureTypeFilter;
      teamMembers: UserFilter;
    }> {}

@model
@HasBookmarkFilters
class BookmarksProductValueChart extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksProductValueChart' = 'BookmarksProductValueChart';
  get typename(): 'BookmarksProductValueChart' {
    return 'BookmarksProductValueChart';
  }

  static internalTypename: 'Bookmarks::ProductValueChart' =
    'Bookmarks::ProductValueChart';
  get internalTypename(): 'Bookmarks::ProductValueChart' {
    return 'Bookmarks::ProductValueChart';
  }

  static bookmarkName = 'Bookmarks::ProductValueChart';

  static inputType = 'ProductValueChartAttributes!';

  static fields = {
    dataGroup: attr({ writable: true, readable: true }),
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    timeGroup: attr({ writable: true, readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
  };
  // End auto-generated

  /**
   * Initialize the filters
   */
  async createFilters() {
    const [releases, projects, teams, initiatives, teamMembers] =
      await Promise.all([
        this.selectedReleases(),
        this.selectedProjects(),
        this.selectedTeams(),
        this.selectedInitiatives(),
        this.selectedUsers(),
      ]);

    this.local.featureTypes = this.filters.featureTypes || [];
    this.local.featureTypeIds = {};

    return {
      dateRange: new DateRangeFilter(this.filters.dateRange),
      releases: new ReleaseFilter(this.filters.releases, releases),
      projects: new ProjectFilter(this.filters.projects, projects),
      teams: new TeamFilter(
        teams.map(([, id]) => id),
        teams
      ),
      initiatives: new InitiativeFilter(this.filters.initiatives, initiatives),
      featureTypes: new FeatureTypeFilter(
        this.local.featureTypes,
        // Here we using the name of the feature type as the key as well
        // so we don't have duplicate feature types show up in the chart legend
        // i.e. Bug from Project 1, Bug from Project 2, etc.
        this.local.featureTypes?.map(ft => [ft, ft])
      ),
      teamMembers: new UserFilter(this.filters.teamMembers, teamMembers),
    };
  }

  canFilter(_scope) {
    return true;
  }

  async selectedReleases(): Promise<[string, RecordId][]> {
    const releaseIds = this.filters.releases || [];

    if (releaseIds.length > 0) {
      const releases = await Release.select(['id', 'name'])
        .merge({ project: ['referencePrefix'] })
        .where({ id: releaseIds })
        .all();

      return releases.map(r => [
        `${r.project.referencePrefix} - ${r.name}`,
        r.id,
      ]);
    }
    return [];
  }

  async selectedProjects(): Promise<[string, RecordId][]> {
    const projectIds = this.filters.projects || [];

    if (projectIds.length > 0) {
      const projects = await Project.select(['id', 'name', 'referencePrefix'])
        .where({ id: projectIds })
        .all();

      return projects.map(p => [p.name, p.id]);
    }

    return [];
  }

  async selectedTeams(): Promise<[string, RecordId][]> {
    if (!this.project?.isProductLine) {
      return [];
    }
    const childIds = this.project.children.map(c => c.id);
    const teamIds = this.filters.teams?.length ? this.filters.teams : childIds;

    if (teamIds.length > 0) {
      const teams = await Project.select(['id', 'name'])
        .where({ id: teamIds })
        .all();

      return teams.map(t => [t.name, t.id]);
    }
    return [];
  }

  async selectedInitiatives(): Promise<[string, any][]> {
    const initiativeIds = this.filters.initiatives || [];

    if (initiativeIds.length > 0) {
      const initiatives = await Initiative.select(['id', 'name'])
        .where({ id: initiativeIds })
        .all();

      return initiatives.map(i => [i.name, i.id || '']);
    }

    return [];
  }

  async availableUsers() {
    if (!this.local.users) {
      this.local.users = await User.select('id', 'name', 'initials', 'email')
        .select({ avatarUrl: { size: aha.enums.AvatarSizeEnum.SIZE_40 } })
        .where({ projectId: this.projectId })
        .all();
    }

    return this.local.users;
  }

  async selectedUsers() {
    const userIds = this.filters.teamMembers || [];

    if (userIds.length > 0) {
      const users = await this.availableUsers();
      return users.map(u => [u.name, u.id || '']);
    }
  }

  async chartData() {
    const dateRange = this.getFilter('dateRange');

    const startDate = dateRange?.startTime.toISOString();
    const endDate = dateRange?.endTime.toISOString();

    const filters = {
      eventType: [aha.enums.RecordEventTypeEnum.RECORD_COMPLETED],
      eventSubject: {
        typename: 'Feature',
      },
      teamId: this.projectId,
    };

    const fragment = new Fragment().fragment(
      new Fragment('recordEvents')
        .argument(
          'filters',
          {
            ...filters,
            createdAt: {
              gt: startDate,
              lt: endDate,
            },
          },
          'RecordEventFilters!'
        )
        .fragment(
          new Fragment('raw')
            .attr('createdAt', 'eventType')
            .fragment(
              new Fragment('feature')
                .attr('id', 'name', 'referenceNum', 'path', 'score')
                .fragment(
                  new Fragment('iteration').attr('id', 'name', 'startDate')
                )
                .fragment(new Fragment('workflowKind').attr('id', 'name'))
                .fragment(
                  new Fragment('release').attr('id', 'name', 'referenceNum')
                )
                .fragment(new Fragment('project').attr('id', 'name'))
                .fragment(new Fragment('team').attr('id'))
                .fragment(
                  new Fragment('initiative').attr(
                    'id',
                    'name',
                    'referenceNum',
                    'color'
                  )
                )
                .fragment(new Fragment('assignedToUser').attr('id', 'name'))
            )
        )
    );

    const data = (await ApplicationModel.query(fragment)) as any;

    this.local.featureTypes = [];

    // Extract a list of unique workflowKinds (feature types) to populate the filter with
    data.recordEvents.raw.forEach(({ feature }) => {
      const { workflowKind } = feature;

      if (!this.local.featureTypes.find(ft => ft[0] === workflowKind.name)) {
        this.local.featureTypes.push([workflowKind.name, workflowKind.name]);
      }

      const existing = this.local.featureTypeIds[workflowKind.name];
      if (existing) {
        if (!existing.includes(workflowKind.id)) {
          existing.push(workflowKind.id);
        }
      } else {
        this.local.featureTypeIds[workflowKind.name] = [workflowKind.id];
      }
    });

    return data;
  }

  /**
   * Updates the list of all possible filter IDs with `options`. For
   * example, will tell the `UserFilter` about all users once the
   * Selectro is first opened.
   */
  setFilterOptions(scope: string, options) {
    // @ts-ignore filters from getFilter are not yet typed
    this.getFilter(scope).setOptions(options);
  }

  get featureTypes() {
    const output = [...this.local.featureTypes];

    const alreadySelected =
      this.getFilter('featureTypes')?.toBookmarkConfiguration();

    // Since we're building up the list of available feature types from the data we're charting
    // it's possible for a previously selected option to "disappear" if we change the other filters
    // in a way that all items with that type are no longer visible. Here we manually reinsert any
    // already selected options that aren't already in the list, so the user can deselect them.
    alreadySelected?.forEach(option => {
      if (!output.find(item => item[0] === option)) {
        output.push([option, option]);
      }
    });

    return output;
  }

  get featureTypeIds() {
    return this.local.featureTypeIds;
  }
}

export default BookmarksProductValueChart;
